import React, { useState, useEffect, useRef } from 'react';
import * as ReactDOM from 'react-dom';
import { DataTable } from 'primereact/datatable';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Column } from 'primereact/column';
import { MultiSelect } from 'primereact/multiselect';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import { ConfirmDialog } from 'primereact/confirmdialog';
import { DATE_STRATEGY, formatDate, isValidTimeStamp } from '../../utils/date';
import './index.css';
import useContextMenu from '../ContextMenu/useContextMenu';
import { ListBox } from 'primereact/listbox';
import { FILTER } from '../../services/common/urlService';
import { createLabelValueObj } from '../ContextMenu/utils';
import useIsMobile from '../../utils/hooks/useIsMobile';

const noop = () => { };
const DownloadOption = createLabelValueObj('View attachment', 'Download');

const GRID = {
  DEFAULTS: {
    PAGINATION: {
      AVAILABLE_PAGE_SIZES: [1, 5, 10, 20, 50],
      TEMPLATE:
        'CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown',
      CURRENT_PAGE_REPORT_TEMPLATE:
        'Showing record {first} to {last} of {totalRecords} records',
    },
  },
};

const RECORD_DELETION_POPUP = {
  TEXT_MESSAGE: 'Record will be deleted forever. Do you wish to continue?',
};

const getRowsPerPageOptions = (pageSize, totalPages, totalRecords) => {
  return GRID.DEFAULTS.PAGINATION.AVAILABLE_PAGE_SIZES.filter(
    (val) => val <= pageSize * totalPages
  );
};

const filterColumFromDataSet = (dataList, dataKey) => {
  return dataList.filter((col) => col.dataKey !== dataKey);
};

const isDeleteAllowed = (screenPermissions) => !!screenPermissions?.delete;
const isCopyAllowed = (screenPermissions) => !!screenPermissions?.create;
const isEditAllowed = (screenPermissions) => !!screenPermissions?.edit;
const isViewAllowed = (screenPermissions) => !!screenPermissions?.view;

const EzTable = (props) => {
  const {
    screenPermissions,
    setDeleteConfirmationPopupVisibility,
    shouldOpenDeleteConfirmationPopup,
    showGlobalFilter = false,
    dataKey = 'id',
    paginationInfo,
    areFiltersVisible,
    columns,
    onDeleteRecord,
    onEditRecord,
    onCopyRecord,
    onViewRecord,
    value,
    emptyMessage = 'No Record found',
    globalFilterFields,
    filters,
    additinalFilter,
    loading,
    sortField = 'date',
    loadLazyData,
    moreActionProps,
    isSearch,
    fetchAttachmentsById = noop(),
    downloadAllAttachments = noop(),
    showMoreActions = true,
    showDeleteButton,
    showCopyButton,
    showViewButton,
    showEditButton,
    showEmailButton,
    showDownloadButton,
    onEmailRecord,
    onDownloadClick,
    onNoShowClick,
    onTableFilterChange,
    stateKey,
    // Initial values for state restoration
    initialSortOrder,
    initialFirst,
    initialRows,
    // Callback to save raw table state for restoration
    onTableStateChange,
    // Row expansion props
    expandedRows,
    onRowToggle,
    rowExpansionTemplate,
    filterDependencies,
  } = props;
  
  const isMobile = useIsMobile();

  // Use ref to always have access to latest additinalFilter prop (avoid stale closure)
  const additinalFilterRef = useRef(additinalFilter);
  useEffect(() => {
    additinalFilterRef.current = additinalFilter;
  }, [additinalFilter]);
  
  const [showDialog, setShowDialog] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const [currentRowData, setCurrentRowData] = useState(null);
  const [mapdata, setMapdata] = useState([]);
  const onDialogHide = () => setShowDialog(false)
  const onMoreActionMenu = (event, currentRowData) => {
    document.dispatchEvent(new MouseEvent("customContextmenu", event))
    setCurrentRowData(currentRowData)
  }
  const {
    isMoreActionDisabled = true,
    downloadEnabled = false,
    moreActionOptions = [],
    onMoreActionChange = null
  } = moreActionProps || {};

  const getMoreActionOption = () => {
    if (!isMoreActionDisabled) {
      if (downloadEnabled && !moreActionOptions.includes(DownloadOption)) moreActionOptions.unshift(DownloadOption)
      return moreActionOptions;
    }
    return [];
  }

  const onMoreActionChangeModified = (option) => {
    if (downloadEnabled && option === DownloadOption.value) {
      setShowDialog(true);
    }
    else onMoreActionChange(option, currentRowData);
  }

  const { anchorPoint, show } = useContextMenu();
  const { pageSize, totalRecords, totalPages, pageNumber } = paginationInfo;

  const getDefaultVisibleColumns = (cols) => {
    if (!cols) return [];
    return cols.filter(col => {
      if (col.dataKey === 'id') return false;
      
      if (col.extraProps?.defaultVisible === false) {
        return false;
      }
      
      if (col.extraProps?.selectable === false && !col.extraProps?.hidden) {
        return true;
      }
      
      return col.extraProps?.selectable !== false;
    });
  };

  const [selectedColumns, setSelectedColumns] = useState(
    getDefaultVisibleColumns(columns)
  );

  // Update selectedColumns when columns prop changes (e.g., when data loads)
  useEffect(() => {
    if (columns && columns.length > 0) {
      setSelectedColumns(getDefaultVisibleColumns(columns));
    }
  }, [columns]);

  const [selectedRow, setSelectedRow] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);

  const cm = useRef(null);
  const dt = useRef(null);
  const toBeDeletedRecordId = useRef(-1);
  // eslint-disable-next-line
  const toBePopupinDialog = useRef(-1);

  const onColumnToggle = (event) => {
    let selectedColumns = event.value;
    
    const nonSelectableVisibleColumns = columns.filter(
      col => col.extraProps?.selectable === false && 
             !col.extraProps?.hidden && 
             col.extraProps?.defaultVisible !== false
    );
    
    const combinedColumns = [...nonSelectableVisibleColumns, ...selectedColumns];
    
    let orderedSelectedColumns = columns.filter((col) =>
      combinedColumns.some((sCol) => sCol.dataKey === col.dataKey)
    );
    
    setSelectedColumns(orderedSelectedColumns);
  };

  const [globalFilterValue, setGlobalFilterValue] = useState('');
  const onGlobalFilterChange = (e) => {
    const value = e.target.value;
    let _filters = { ...filters };
    _filters['global'].value = value;

    setGlobalFilterValue(value);
  };

const renderHeader = () => {
  const selectableColumns = filterColumFromDataSet(columns, 'id').filter(
    col => col.extraProps?.selectable !== false
  );
  
  const selectableValue = selectedColumns.filter(
    col => col.extraProps?.selectable !== false
  );
  
  return (
    <div className="grid-multi-select-header">
      <div style={{ textAlign: 'left', width: '100%' }}>
        <MultiSelect
          className={'grid-multi-select-wrapper'}
          panelClassName={''}
          display={'chip'}
          value={selectableValue}
          options={selectableColumns}
          optionLabel="colLabel"
          onChange={onColumnToggle}
          maxSelectedLabels={isMobile ? 1 : 2}
          selectedItemsLabel="{0} columns selected"
          style={{ width: '100%' }}
        />
      </div>
      {showGlobalFilter && (
        <span className="p-input-icon-left" style={{ width: '100%' }}>
          <i className="pi pi-search" />
          <InputText
            value={globalFilterValue}
            onChange={onGlobalFilterChange}
            placeholder="Global search in table"
          />
        </span>
      )}
    </div>
  );
};

  const [lazyParams, setLazyParams] = useState(() => {
    return {
      first: initialFirst || 0,
      rows: initialRows || pageSize,
      page: initialFirst ? Math.floor(initialFirst / (initialRows || pageSize)) : 0,
      sortField,
      sortOrder: initialSortOrder || null,
      filters: filters,
      additinalFilter: additinalFilter
    };
  });

  // isFirstRun ref kept for potential future use
  const isFirstRun = useRef(true);

  const applyFilterDependencies = (newFilters, oldFilters) => {
    if (!filterDependencies || !oldFilters) return newFilters;
    
    let modifiedFilters = { ...newFilters };
    
    Object.entries(filterDependencies).forEach(([childKey, parentKey]) => {
      const parentWasSet = oldFilters[parentKey]?.value !== null && oldFilters[parentKey]?.value !== undefined;
      const parentIsNowCleared = newFilters[parentKey]?.value === null || newFilters[parentKey]?.value === undefined;
      
      if (parentWasSet && parentIsNowCleared) {
        modifiedFilters[childKey] = { ...modifiedFilters[childKey], value: null };
      }
    });
    
    return modifiedFilters;
  };

  const getFetchDataParams = (params) => {
    // Use passed params instead of stale lazyParams closure
    let { sortOrder, sortField, page, rows, filters } = params || lazyParams;
    let modifiedFilters = [];
    if (filters) {
      const filterKeys = Object.keys(filters);
      modifiedFilters = filterKeys.reduce((acc, filterKey) => {
        if (filters[filterKey]?.value) {
          const { matchStrategy, valueMapper, value } = filters[filterKey];
          
          // Helper to check if a string looks like an ISO date (contains 'T' and ends with 'Z' or timezone offset)
          const isISODateString = (str) => {
            if (typeof str !== 'string') return false;
            // Check for ISO 8601 date format: 2026-02-05T05:00:00.000Z or with timezone offset
            return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(str);
          };
          
          // Handle date values - format them properly to YYYY-MM-DD for backend
          let queryValue = value;
          if (valueMapper) {
            queryValue = valueMapper(value);
          } else if (value instanceof Date) {
            // Date object - format to ISO date (YYYY-MM-DD) for backend
            queryValue = formatDate(value, DATE_STRATEGY.DATE_ISO);
          } else if (isISODateString(value)) {
            // ISO date string (e.g., from saved state) - parse and format to YYYY-MM-DD
            queryValue = formatDate(new Date(value), DATE_STRATEGY.DATE_ISO);
          } else if (typeof value === 'string' && !isNaN(Date.parse(value)) && value.includes('GMT')) {
            // GMT date string from localStorage - parse and format
            queryValue = formatDate(new Date(value), DATE_STRATEGY.DATE_ISO);
          }
          
          return [
            ...acc,
            {
              filterField: filters[filterKey]?.filterKey || filterKey,
              matchStrategy: matchStrategy || FILTER.MATCH_STRATEGY.CONTAINS,
              query: queryValue,
            },
          ];
        }
        return acc;
      }, modifiedFilters);
    }

    return {
      filters: filters && modifiedFilters,
      sortOrder,
      sortField,
      pageSize: rows || pageSize,
      pageNumber: page ?? 0,
      additinalFilter: additinalFilterRef.current || additinalFilter
    };
  };

  const editRecord = (rowData) => {
    onEditRecord(rowData.id);
  };
  const copyRecord = (rowData) => {
    onCopyRecord(rowData.id);
  };
  const viewRecord = (rowData) => {
    onViewRecord(rowData.id);
  };
  const deleteRecord = () => {
    let params = getFetchDataParams(lazyParams);
    const { pageNumber, pageSize } = params;

    let newPageNumber = pageNumber;
    if (totalRecords - 1 === 0) {
      newPageNumber = 0;
    } else if (totalRecords - 1 <= pageNumber * pageSize) {
      newPageNumber = pageNumber - 1;
    }
    onDeleteRecord({
      params: {
        ...params,
        pageNumber: newPageNumber,
      },
      id: toBeDeletedRecordId.current,
    });
  };

  const onPage = (event) => {
    const { first, rows, page } = event;
    const newParams = { ...lazyParams, first, rows, page };
    setLazyParams(newParams);
    loadLazyData(getFetchDataParams(newParams));
    if (onTableStateChange) {
      onTableStateChange(newParams);
    }
  };

  const onSort = (event) => {
    const { sortField, sortOrder } = event;
    const newParams = { ...lazyParams, sortField, sortOrder };
    setLazyParams(newParams);
    loadLazyData(getFetchDataParams(newParams));
    if (onTableStateChange) {
      onTableStateChange(newParams);
    }
  };

  const onFilter = (event) => {
    const filtersWithDependencies = applyFilterDependencies(event.filters, lazyParams.filters);
    const newParams = { ...lazyParams, filters: filtersWithDependencies, page: 0, first: 0 };
    setLazyParams(newParams);
    loadLazyData(getFetchDataParams(newParams));
    if (onTableFilterChange) {
      onTableFilterChange(filtersWithDependencies);
    }
    if (onTableStateChange) {
      onTableStateChange(newParams);
    }
  };

  const actionBodyTemplate = (rowData) => {

    const btnClass =
      'p-button-sm p-button-raised p-component p-button-rounded p-button-text p-button-icon-only';
    return (
      <React.Fragment>
        {isEditAllowed(screenPermissions) && showEditButton &&(
          <Button
            icon="pi pi-pencil"
            className={`${btnClass} mr-2`}
            onClick={() => {
              editRecord(rowData);
            }}
          />
        )}
        {isDeleteAllowed(screenPermissions) && showDeleteButton && (
          <Button
            icon="pi pi-trash"
            className={`${btnClass} p-button-danger`}
            onClick={() => {
              toBeDeletedRecordId.current = rowData.id;
              setDeleteConfirmationPopupVisibility(true);
            }}
          />
        )}
        {isCopyAllowed(screenPermissions) && showCopyButton && (
          <Button
            icon="pi pi-copy"
            className={`${btnClass} mr-2`}
            onClick={() => {
              copyRecord(rowData);
            }}
          />
        )}
        {isViewAllowed(screenPermissions) && showViewButton && (
          <Button
            icon="pi pi-eye"
            className={`${btnClass}`}
            onClick={() => {
              viewRecord(rowData);
            }}
          />
        )}
        {showEmailButton && (
          <Button
            icon="pi pi-envelope"
            className={`${btnClass} mr-2`}
            tooltip="Email Report"
            tooltipOptions={{ position: 'bottom' }}
            onClick={() => {
              onEmailRecord && onEmailRecord(rowData);
            }}
          />
        )}
        {showDownloadButton && (
          <Button
            icon="pi pi-download"
            className={`${btnClass} mr-2`}
            tooltip="Download Invoice"
            tooltipOptions={{ position: 'bottom' }}
            onClick={() => {
              onDownloadClick && onDownloadClick(rowData);
            }}
          />
        )}
        {
          showMoreActions && <Button
            disabled={isMoreActionDisabled}
            tooltip='More Action'
            tooltipOptions={{ position: 'bottom', className: 'green-tooltip' }}
            icon="pi pi-ellipsis-v"
            className={`${btnClass} p-button-info ml-2`}
            onClick={(event) => onMoreActionMenu(event, rowData)}
          />
        }
        {
          show && ReactDOM.createPortal(
            <ListBox className="context-menu"
              style={{ top: anchorPoint.y, left: anchorPoint.x }}
              options={getMoreActionOption()}
              onChange={(e) => onMoreActionChangeModified(e.target.value)} />
            , document.body.appendChild(document.createElement("DIV")))
        }
      </React.Fragment>
    );
  };


  const getActionBodyColumn = () => {
    return {
      body: actionBodyTemplate,
      field: 'action',
      header: 'Actions',
      sortable: false,
      hidden: false,
    };
  };

  const dataTableProps = {
    ref: dt,
    header: renderHeader(),
    dataKey,
    selectionMode: 'multiple',
    size: 'small',
    stripedRows: true,
    disabled: false,
    resizableColumns: true,
    reorderableColumns: true,
    showGridlines: false,
    responsiveLayout: 'scroll',
    value,
    removableSort: true,
    emptyMessage,
    globalFilterFields,
    ...(areFiltersVisible ? { filterDisplay: 'row', filterDelay: 500 } : {}),
    ...(stateKey ? { stateStorage: 'local', stateKey: stateKey } : {}),
    paginator: true,
    paginatorTemplate: GRID.DEFAULTS.PAGINATION.TEMPLATE,
    currentPageReportTemplate:
      GRID.DEFAULTS.PAGINATION.CURRENT_PAGE_REPORT_TEMPLATE,
    rowsPerPageOptions: getRowsPerPageOptions(
      pageSize,
      totalPages,
      totalRecords
    ),
    rows: pageSize,
    contextMenuSelection: selectedRow,
    onContextMenuSelectionChange: (e) => setSelectedRow(e.value),
    onContextMenu: (e) => cm.current?.show(e.originalEvent),
    selection: selectedRows,
    onSelectionChange: (e) => setSelectedRows(e.value),
    lazy: true,
    totalRecords,
    onSort,
    onFilter,
    onPage,
    first: lazyParams.first,
    sortField: lazyParams.sortField,
    sortOrder: lazyParams.sortOrder,
    filters: lazyParams.filters,
    loading,
    ...(expandedRows !== undefined ? { expandedRows } : {}),
    ...(onRowToggle ? { onRowToggle } : {}),
    ...(rowExpansionTemplate ? { rowExpansionTemplate } : {}),
  };

  const handleMap = (mapdata) => {
    setShowMap(!showMap)
    let mpdata = [mapdata]
    mpdata = mpdata.map(item => ({
      ...item,
      lat: item.latitude,
      lng: item.longitude,
      name: item.type,
      address: item.logfor
    }))
    setMapdata(mpdata)
  }


  const dropdownBodyTemplate = (dataKey, rowData) => {
    console.log("dataKey", dataKey)
    const dateValue = rowData[dataKey]
    return (
      <span style={{ display: 'flex' }}>
        <span style={{ alignContent: 'center' }}>
          {dateValue}
        </span>
      </span>
    );
  };
  const dateBodyTemplate = (dataKey, rowData, strategy) => {
    const dateValue = rowData[dataKey];
    // Always try to format the date if a strategy is provided
    // dayjs can parse various date formats including "MM/DD/YYYY hh:mmA"
    const value = isValidTimeStamp(dateValue) && strategy ? formatDate(dateValue, strategy) : dateValue;

    return (
      <span style={{ display: 'flex' }}>
        <span style={{ alignContent: 'center' }}>
          {value}
        </span>
      </span>
    );
  };
  const mapBodyTemplate = (dataKey, rowData) => {
    const btnClass =
      'p-button-sm p-button-raised p-component p-button-rounded p-button-text p-button-icon-only';
    return (

      <Button
        icon="pi pi-map-marker"
        className={`${btnClass} mr-2`}
        onClick={() => {
          handleMap(rowData);
        }}
      />

    );
  };
  const monthNavigatorTemplate = (e) => {
    return (
      <Dropdown
        value={e.value}
        options={e.options}
        optionLabel="label"
        optionValue="value"
        onChange={(event) => {
          e.onChange(event.originalEvent, event.value);
        }}
        style={{ lineHeight: 1 }}
      />
    );
  };

  const yearNavigatorTemplate = (e) => {
    return (
      <Dropdown
        value={e.value}
        options={e.options}
        onChange={(event) => e.onChange(event.originalEvent, event.value)}
        className="ml-2"
        style={{ lineHeight: 1 }}
      />
    );
  };
  const dropdownFilterTemplate = (e, options, sortField, disabled = false) => {
    return (
      <Dropdown
        value={e.value}
        name={e.name}
        options={options}
        editable
        filter
        filterBy="label"
        filterInputAutoFocus={true}
        showClear
        placeholder={disabled ? "Select location first" : "Search..."}
        disabled={disabled}
        onChange={(e) => {
          onDropdownChange(e.value, sortField);
        }}
        style={{ lineHeight: 1, minWidth: '120px' }}
      />
    );
  };

  const dateFilterTemplate = (options) => {
    // Get current value from filter state (handles restored state from localStorage)
    const currentValue = options.value ? new Date(options.value) : null;
    
    return (
      <Calendar
        monthNavigator
        yearNavigator
        monthNavigatorTemplate={monthNavigatorTemplate}
        yearNavigatorTemplate={yearNavigatorTemplate}
        yearRange={'2020:2040'}
        showIcon
        value={currentValue}
        onChange={(e) => {
          options.filterCallback(e.value);
          filterDate(e.value, options);
        }}
        dateFormat="dd/mm/yy"
        placeholder="Select date"
        selectOtherMonths={true}
        panelClassName="ez-calendar-panel"
      />
    );
  };
  const filterDate = (calendarValue, options) => {
    const newFilters = {
      ...lazyParams.filters,
      [options.field]: calendarValue ? {
        value: calendarValue,
        filterKey: options.field,
        valueMapper: (date) => formatDate(date, DATE_STRATEGY.DATE_ISO),
      } : { value: null },
    };
    const newParams = { ...lazyParams, filters: newFilters, page: 0, first: 0 };
    setLazyParams(newParams);
    loadLazyData(getFetchDataParams(newParams));
    if (onTableFilterChange) {
      onTableFilterChange(newFilters);
    }
    if (onTableStateChange) {
      onTableStateChange(newParams);
    }
  };

  // const filterDropdown = (dropdown, sortField) => {
  //   console.log("filters", filters)
  //   if (dropdown) {
  //     setLazyParams({
  //       ...lazyParams,
  //       filters: {
  //         ...filters,
  //         [sortField]: { value: dropdown }
  //       },
  //     });
  //   }
  // };

  const onDropdownChange = (dropdownValue, sortField) => {
    const rawFilters = {
      ...lazyParams.filters,
      [sortField]: { ...lazyParams.filters[sortField], value: dropdownValue }
    };
    const newFilters = applyFilterDependencies(rawFilters, lazyParams.filters);
    const newParams = { ...lazyParams, filters: newFilters, page: 0, first: 0 };
    setLazyParams(newParams);
    loadLazyData(getFetchDataParams(newParams));
    if (onTableFilterChange) {
      onTableFilterChange(newFilters);
    }
    if (onTableStateChange) {
      onTableStateChange(newParams);
    }
  };
  const getFilterProps = (extraProps, dataKey) => {
    if (extraProps) {
      const { isDateField, isDropDownField, shouldFilter, showFilterMenu, isMapField, strategy, options, sortField, disabled } = extraProps;
      let enrichedFilterProps = {
        filter: shouldFilter ?? false,
        showFilterMenu: showFilterMenu ?? false,
        filterField: dataKey,
        filterPlaceholder: extraProps?.filterPlaceholder || "Search...",
      };
      if (isDropDownField) {
        enrichedFilterProps = {
          ...enrichedFilterProps,
          //body: (rowData) => dropdownBodyTemplate(dataKey, rowData),
          filterElement: (e) => dropdownFilterTemplate(e, options, sortField, disabled),
        };

      }
      if (isDateField) {
        enrichedFilterProps = {
          ...enrichedFilterProps,
          dataType: 'date',
          filterField: dataKey,
          body: (rowData) => dateBodyTemplate(dataKey, rowData, strategy),
          filterElement: dateFilterTemplate,
        };
      }
      if (isMapField) {
        enrichedFilterProps = {
          body: (rowData) => mapBodyTemplate(dataKey, rowData),
        };
      }


      return enrichedFilterProps;
    }

    return {};
  };
  const showActionColumn = () => {
    // Check if any action buttons would actually be shown
    const hasEditAction = isEditAllowed(screenPermissions) && showEditButton;
    const hasDeleteAction = isDeleteAllowed(screenPermissions) && showDeleteButton;
    const hasViewAction = isViewAllowed(screenPermissions) && showViewButton;
    const hasCopyAction = isCopyAllowed(screenPermissions) && showCopyButton;
    const hasEmailAction = showEmailButton;
    const hasDownloadAction = showDownloadButton;
    
    return showMoreActions || hasEditAction || hasDeleteAction || hasViewAction || hasCopyAction || hasEmailAction || hasDownloadAction;
  }

  const getColumnProps = (column) => {
    const filterProps = getFilterProps(column?.extraProps, column.dataKey);
    return {
      ...column,
      field: column.dataKey,
      header: column.colLabel,
      sortable: column?.extraProps?.isSortable,
      sortField: column?.extraProps?.sortField,
      options: column?.extraProps?.options,
      hidden: column?.extraProps?.hidden,
      body: (rowData) => {
        let columnContent;
        if (column.dataKey === "imgUrl") {
          columnContent = (
            rowData.imgUrl !== "-" ?
              <img className='imgbox' src={rowData.imgUrl} />
              : <span></span>
          );
        }
        else if (column.dataKey === "status") {
          let statusColor;
          const status = rowData.status?.toLowerCase();
          
          if (status === "cancelled" || status === "canceled") {
            statusColor = "danger";
          } else if (status === "confirmed" || status === "paid") {
            statusColor = "success";
          } else if (status === "no show" || status === "noshow") {
            statusColor = "warning";
          } else {
            statusColor = null;
          }

          if (statusColor) {
            columnContent = (
              <div
                style={{ width: "100%", textAlign: "center" }}
                className={`status-${statusColor} p-1`}
              >
                {rowData.status}
              </div>
            );
          } else {
            columnContent = rowData.status;
          }
        }
        else if (column.dataKey === "title" && rowData.isDefault === "Yes") {
          columnContent = (
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <span>{rowData.title}</span>
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  backgroundColor: "#22c55e",
                  color: "white",
                  padding: "2px 8px",
                  borderRadius: "10px",
                  fontSize: "10px",
                  fontWeight: "600",
                }}
              >
                Default
              </span>
            </div>
          );
        }
        else if (column?.extraProps?.isNoShowColumn) {
          const status = rowData.status?.toLowerCase();
          const isAlreadyNoShow = status === "no show" || status === "noshow";
          const isCancelled = status === "cancelled";
          
          if (isAlreadyNoShow || isCancelled) {
            columnContent = <span style={{ color: "#6b7280", fontSize: "12px" }}>—</span>;
          } else if (onNoShowClick) {
            columnContent = (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onNoShowClick(rowData.id);
                }}
                style={{
                  fontSize: "12px",
                  color: "#3c5a98",
                  background: "none",
                  border: "none",
                  textDecoration: "underline",
                  cursor: "pointer",
                  padding: "4px 0",
                  whiteSpace: "nowrap",
                }}
              >
                Mark No Show
              </button>
            );
          } else {
            columnContent = <span>—</span>;
          }
        }
        else if (column?.extraProps?.isDateField && column?.extraProps?.strategy) {
          // Use dateBodyTemplate for date fields with a strategy
          columnContent = dateBodyTemplate(column.dataKey, rowData, column.extraProps.strategy);
        }
        else {
          columnContent = column?.extraProps?.body
            ? column?.extraProps?.body(rowData)
            : rowData[column.dataKey];
        }

        return (
          <div
            title={rowData[column.dataKey]}
            style={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: "200px"
            }}
          >{columnContent}</div>)
          ;
      },
      ...filterProps,
    };
  };


  return (
    <div>
      <div className="card">
        <DataTable key={`datatable-${value?.length}-${value?.[0]?.id}`} {...dataTableProps}
        >
          {rowExpansionTemplate && <Column expander style={{ width: '3em' }} />}
          {showActionColumn() && <Column key="action-column" {...getActionBodyColumn()} />}
          {/* <Column selectionMode="multiple" headerStyle={{width: '3em'}}></Column> */}
          {selectedColumns.map((column) => (
            <Column key={column.dataKey} {...getColumnProps(column)} />
          ))}
          {/* {showActionColumn() && <Column {...getActionBodyColumn()} />} */}
        </DataTable>
      </div>
      <ConfirmDialog
        visible={shouldOpenDeleteConfirmationPopup}
        message={RECORD_DELETION_POPUP.TEXT_MESSAGE}
        header="Please Confirm Deletion"
        position={'top'}
        onHide={() => setDeleteConfirmationPopupVisibility(false)}
        acceptLabel={'Cancel'}
        rejectLabel={'Delete Record'}
        acceptClassName={'p-button-raised'}
        rejectClassName={'p-button-text p-button-danger'}
        reject={() => {
          deleteRecord();
        }}
        accept={() => setDeleteConfirmationPopupVisibility(false)}
      />


    </div>
  );
};

export default EzTable;
